/**
 * Ernaehrung im Ueberblick (F-35 C) - rein und getestet: Tagesziele je nach
 * Trainings- oder Ruhetag, Makro-Verteilung, Portionen je Lebensmittelgruppe,
 * Zielerreichung und Wochen-/Monatsverlauf. Bewertet wird nur gegen Oles
 * eigene Ziele; einzelne Abweichungen bleiben neutral (siehe D).
 */
import type { LocalDate } from '@/domain/types';
import { addieren, getraenkeWerte, tagWerte, type Daten, type Summe } from '@/domain/ernaehrung/naehrwerte';
import type { ErnaehrungsEinstellungen, Getraenk, LebensmittelGruppe, Mahlzeit, Naehrwerte, Tagesziele } from '@/domain/ernaehrung/typen';

/** Gruppen, die als Portionen gezaehlt werden (Getraenke und Sonstiges nicht). */
export const GEZAEHLTE_GRUPPEN: LebensmittelGruppe[] = ['gemuese', 'obst', 'vollkorn', 'eiweiss', 'milch', 'suess', 'fastfood'];

export const STANDARD_SPANNE = 10;
export const STANDARD_MINDEST_MAHLZEITEN = 3;

/** Wie ein Ziel gemeint ist: Spanne um den Wert, Mindestens oder Hoechstens. */
export const ZIEL_ART: Record<keyof Tagesziele, 'spanne' | 'mindestens' | 'hoechstens'> = {
  kcal: 'spanne', kh: 'spanne', fett: 'spanne',
  eiweiss: 'mindestens', ballast: 'mindestens', wasserMl: 'mindestens',
  zucker: 'hoechstens', salz: 'hoechstens',
};

/** Ziele des Tages: an Trainingstagen ueberschreiben die Trainingsziele die allgemeinen. */
export function zieleFuer(e: Pick<ErnaehrungsEinstellungen, 'ziele' | 'zieleTraining'> | undefined, training: boolean): Tagesziele {
  const basis = { ...e?.ziele };
  if (!training || !e?.zieleTraining) return basis;
  for (const [k, v] of Object.entries(e.zieleTraining) as [keyof Tagesziele, number | undefined][]) if (v !== undefined) basis[k] = v;
  return basis;
}

/** Ziel erreicht? `undefined`, wenn kein Ziel oder kein Wert. */
export function erreicht(k: keyof Tagesziele, wert: number | undefined, ziel: number | undefined, spanne = STANDARD_SPANNE): boolean | undefined {
  if (ziel === undefined || wert === undefined) return undefined;
  const art = ZIEL_ART[k];
  if (art === 'mindestens') return wert >= ziel;
  if (art === 'hoechstens') return wert <= ziel;
  return Math.abs(wert - ziel) <= ziel * (spanne / 100);
}

/** Anteile von Eiweiss, Kohlenhydraten und Fett an deren Energie (4/4/9 kcal pro g), in Prozent. */
export function makroVerteilung(w: Naehrwerte): { eiweiss: number; kh: number; fett: number } | null {
  const e = (w.eiweiss ?? 0) * 4, k = (w.kh ?? 0) * 4, f = (w.fett ?? 0) * 9;
  const summe = e + k + f;
  if (summe <= 0) return null;
  // Groesste Reste: die drei Anteile ergeben immer genau 100 %.
  const roh = [e, k, f].map((x) => (x / summe) * 100);
  const ganz = roh.map(Math.floor);
  const rest = 100 - ganz.reduce((a, b) => a + b, 0);
  roh.map((x, i) => [x - ganz[i]!, i] as const).sort((a, b) => b[0] - a[0]).slice(0, rest).forEach(([, i]) => { ganz[i]!++; });
  return { eiweiss: ganz[0]!, kh: ganz[1]!, fett: ganz[2]! };
}

/**
 * Portionen je Gruppe. Ein Lebensmittel mit Standardportion zaehlt nach
 * Menge (auf halbe Portionen gerundet, mindestens eine halbe), sonst als 1.
 * Ein Rezept zaehlt je gegessener Portion einmal fuer seine Gruppe bzw. -
 * ohne Gruppe - einmal fuer jede Gruppe seiner Zutaten.
 */
export function gruppenPortionen(mahlzeiten: Mahlzeit[], d: Daten): Partial<Record<LebensmittelGruppe, number>> {
  const out: Partial<Record<LebensmittelGruppe, number>> = {};
  const plus = (g: LebensmittelGruppe | undefined, n: number) => { if (g && GEZAEHLTE_GRUPPEN.includes(g)) out[g] = (out[g] ?? 0) + n; };
  for (const p of mahlzeiten.flatMap((m) => m.posten)) {
    if (p.art === 'lebensmittel') {
      const l = d.lebensmittel.get(p.refId);
      const portion = l?.portionen?.[0]?.menge;
      plus(l?.gruppe, portion ? Math.max(0.5, Math.round((p.menge / portion) * 2) / 2) : 1);
    } else if (p.art === 'rezept') {
      const r = d.rezepte.get(p.refId);
      if (!r) continue;
      if (r.gruppe) plus(r.gruppe, p.menge);
      else for (const g of new Set(r.zutaten.map((z) => d.lebensmittel.get(z.lebensmittelId)?.gruppe))) plus(g, p.menge);
    }
  }
  return out;
}

export interface TagEingabe {
  datum: LocalDate;
  gegessen?: { mahlzeiten: Mahlzeit[]; vollstaendig?: boolean };
  plan?: { mahlzeiten: Mahlzeit[] };
  getraenke: Getraenk[];
  training: boolean;
}

export interface TagZeile {
  datum: LocalDate;
  training: boolean;
  /** Mahlzeiten mit mindestens einem Eintrag. */
  mahlzeiten: number;
  vollstaendig: boolean;
  summe: Summe;
  wasserMl: number;
  planKcal?: number;
  ziele: Tagesziele;
  imZiel: Partial<Record<keyof Tagesziele, boolean>>;
}

const wertFuer = (k: keyof Tagesziele, w: Naehrwerte, wasser: number) => (k === 'wasserMl' ? wasser : w[k]);

export function tagZeile(t: TagEingabe, d: Daten, e?: ErnaehrungsEinstellungen): TagZeile {
  const mz = t.gegessen?.mahlzeiten ?? [];
  const g = getraenkeWerte(t.getraenke, d);
  const summe = g ? addieren([tagWerte(mz, d), g]) : tagWerte(mz, d);
  const wasserMl = t.getraenke.reduce((s, x) => s + x.ml, 0);
  const mahlzeiten = mz.filter((m) => m.posten.length).length;
  const ziele = zieleFuer(e, t.training);
  const imZiel: TagZeile['imZiel'] = {};
  for (const k of Object.keys(ziele) as (keyof Tagesziele)[]) {
    const r = erreicht(k, wertFuer(k, summe.werte, wasserMl), ziele[k], e?.spanne);
    if (r !== undefined) imZiel[k] = r;
  }
  const planPosten = t.plan?.mahlzeiten.some((m) => m.posten.length);
  return {
    datum: t.datum, training: t.training, mahlzeiten, summe, wasserMl, ziele, imZiel,
    vollstaendig: t.gegessen?.vollstaendig ?? mahlzeiten >= (e?.mindestMahlzeiten ?? STANDARD_MINDEST_MAHLZEITEN),
    ...(planPosten ? { planKcal: tagWerte(t.plan!.mahlzeiten, d).werte.kcal ?? 0 } : {}),
  };
}

const mittel = (xs: number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : undefined);

export interface Zusammenfassung {
  /** Tage mit mindestens einem Eintrag bzw. vollstaendig erfasst. */
  mitEintrag: number;
  vollstaendig: number;
  /** Durchschnitt nur ueber vollstaendige Tage. */
  mittel: Naehrwerte & { wasserMl?: number };
  /** Je Ziel: an wie vielen vollstaendigen Tagen erreicht, von wie vielen mit Ziel. */
  imZiel: Partial<Record<keyof Tagesziele, { ja: number; von: number }>>;
  /** Plan und Wirklichkeit an Tagen mit beidem: Ø kcal geplant vs. gegessen. */
  planVergleich: { tage: number; plan?: number; gegessen?: number };
}

export function zusammenfassen(zeilen: TagZeile[]): Zusammenfassung {
  const voll = zeilen.filter((z) => z.vollstaendig);
  const m: Zusammenfassung['mittel'] = {};
  for (const k of ['kcal', 'eiweiss', 'kh', 'zucker', 'fett', 'gesFett', 'ballast', 'salz'] as const) {
    const v = mittel(voll.flatMap((z) => (z.summe.werte[k] !== undefined ? [z.summe.werte[k]!] : [])));
    if (v !== undefined) m[k] = v;
  }
  const w = mittel(zeilen.filter((z) => z.wasserMl > 0).map((z) => z.wasserMl));
  if (w !== undefined) m.wasserMl = w;
  const imZiel: Zusammenfassung['imZiel'] = {};
  for (const z of voll) {
    for (const [k, ja] of Object.entries(z.imZiel) as [keyof Tagesziele, boolean][]) {
      const x = imZiel[k] ?? { ja: 0, von: 0 };
      x.von++; if (ja) x.ja++;
      imZiel[k] = x;
    }
  }
  const beide = voll.filter((z) => z.planKcal !== undefined);
  return {
    mitEintrag: zeilen.filter((z) => z.mahlzeiten > 0).length, vollstaendig: voll.length, mittel: m, imZiel,
    planVergleich: { tage: beide.length, plan: mittel(beide.map((z) => z.planKcal!)), gegessen: mittel(beide.map((z) => z.summe.werte.kcal ?? 0)) },
  };
}
